import type { AppDispatch } from 'src/client/store'
import { getLereDynamicPricingGroup, isEligibleForLereDynamicPricingAbTest } from 'checkout/lib/utils/lereDynamicPricing'
import { getCachedEligibleRoomRateIds, getEligibleRoomRateIds } from 'lib/lere/eligibleRoomRates'
import { hasEligibleFlashSearchResult } from 'lib/lere/isFlashSearchResultEligibleForLereDynamicPricing'
import { ensureLereGroupAssignment } from 'lib/lere/lereGroupAssignment'
import { lereDecisionSessionState, type LereDynamicPricingAuthSession } from 'lib/lere/lereDynamicPricingLabSession'
import { hasLereLabDistinctId } from 'lib/lab/labDistinctId'
import getOfferListKey from 'lib/offer/offerListKey'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'

const decidedOfferListKeys = new Set<string>()
const evaluationPromises = new Map<string, Promise<LereSearchPricingEvaluationResult>>()
const queuedEvaluationKeys = new Set<string>()

export type LereSearchPricingEvaluationResult = {
  assignedLereGroup?: string
  didAttemptDecide: boolean
}

const NO_LERE_SEARCH_PRICING_EVALUATION: LereSearchPricingEvaluationResult = {
  didAttemptDecide: false,
}

export function clearLereSearchPricingTrigger(offerListKey: string): void {
  decidedOfferListKeys.delete(offerListKey)
  evaluationPromises.delete(offerListKey)
  queuedEvaluationKeys.delete(offerListKey)
}

interface EvaluateLereSearchPricingParams {
  dispatch: AppDispatch
  getState: () => App.State
  filters: App.OfferListFilters
  isLerePricingRefresh: boolean
  onEligibleSurchargedResultFound?: () => void
}

/** Sync subset of evaluateLereSearchPricing eligibility; keep in sync with that function. */
export function isPendingLereSearchPricingDecision(
  state: App.State,
  filters: App.OfferListFilters,
): boolean {
  if (!isEligibleForLereDynamicPricingAbTest()) {
    return false
  }

  if (!getShouldUseSearchPrices(filters) || !filters.checkIn || !filters.checkOut) {
    return false
  }

  const offerListKey = getOfferListKey(filters)

  if (decidedOfferListKeys.has(offerListKey)) {
    return false
  }

  if (getLereDynamicPricingGroup(state)) {
    return false
  }

  const authSession: LereDynamicPricingAuthSession = state.auth.account?.memberId ? 'loggedIn' : 'loggedOut'

  if (lereDecisionSessionState[authSession].completed) {
    return false
  }

  if (!hasLereLabDistinctId(state)) {
    return false
  }

  return true
}

/** Whether streamed metadata has a surcharged room rate, or null if eligible rates are not cached yet. */
export function hasEligibleSurchargedSearchResultInState(
  state: App.State,
  filters: App.OfferListFilters,
): boolean | null {
  if (!isPendingLereSearchPricingDecision(state, filters) || !filters.checkIn || !filters.checkOut) {
    return false
  }

  const cachedRoomRateIds = getCachedEligibleRoomRateIds(filters.checkIn, filters.checkOut)
  if (!cachedRoomRateIds) {
    return null
  }

  const offerListKey = getOfferListKey(filters)
  const offerMetaData = state.offer.searchResultMetadata.offerMetaData[offerListKey]

  return hasEligibleFlashSearchResult(offerMetaData, cachedRoomRateIds)
}

async function evaluateLereSearchPricing({
  dispatch,
  getState,
  filters,
  isLerePricingRefresh,
  onEligibleSurchargedResultFound,
}: EvaluateLereSearchPricingParams): Promise<LereSearchPricingEvaluationResult> {
  if (isLerePricingRefresh) {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  const state = getState()

  if (!isPendingLereSearchPricingDecision(state, filters) || !filters.checkIn || !filters.checkOut) {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  const offerListKey = getOfferListKey(filters)

  // Always resolve: callers block on this promise; failures mean no group.
  let eligibleRoomRateIds: Set<string>
  try {
    eligibleRoomRateIds = await getEligibleRoomRateIds(filters.checkIn, filters.checkOut)
  } catch {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  const offerMetaData = getState().offer.searchResultMetadata.offerMetaData[offerListKey]

  if (!hasEligibleFlashSearchResult(offerMetaData, eligibleRoomRateIds)) {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  if (decidedOfferListKeys.has(offerListKey)) {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  // Eligibility fetching can span a login or consent change; recheck before requesting a decision.
  if (!hasLereLabDistinctId(getState())) {
    return NO_LERE_SEARCH_PRICING_EVALUATION
  }

  decidedOfferListKeys.add(offerListKey)
  onEligibleSurchargedResultFound?.()

  const latestState = getState()
  const authSession: LereDynamicPricingAuthSession = latestState.auth.account?.memberId ? 'loggedIn' : 'loggedOut'

  let assignedLereGroup: string | undefined
  try {
    assignedLereGroup = await ensureLereGroupAssignment({
      dispatch,
      authSession,
      getLereGroup: () => getLereDynamicPricingGroup(getState()),
      hasDistinctId: hasLereLabDistinctId(latestState),
    })
  } catch {
    decidedOfferListKeys.delete(offerListKey)
    return { didAttemptDecide: true }
  }

  const hasCompletedDecision = lereDecisionSessionState[authSession].completed

  if (!assignedLereGroup && !hasCompletedDecision) {
    decidedOfferListKeys.delete(offerListKey)
  }

  return {
    didAttemptDecide: true,
    assignedLereGroup,
  }
}

function trackEvaluation(offerListKey: string, evaluation: Promise<LereSearchPricingEvaluationResult>): void {
  evaluationPromises.set(offerListKey, evaluation)
  evaluation.finally(() => {
    if (evaluationPromises.get(offerListKey) === evaluation) {
      evaluationPromises.delete(offerListKey)
    }
  }).catch(() => {})
}

export function evaluateLereSearchPricingForSearchResults(
  params: EvaluateLereSearchPricingParams,
): Promise<LereSearchPricingEvaluationResult> {
  const offerListKey = getOfferListKey(params.filters)
  const existingEvaluation = evaluationPromises.get(offerListKey)

  if (existingEvaluation) {
    if (queuedEvaluationKeys.has(offerListKey)) {
      return existingEvaluation
    }

    // Queue one follow-up when metadata arrives during an in-flight evaluation.
    queuedEvaluationKeys.add(offerListKey)
    const chainedEvaluation = existingEvaluation.then((result) => {
      queuedEvaluationKeys.delete(offerListKey)
      if (result.assignedLereGroup) {
        return result
      }
      return evaluateLereSearchPricing(params)
    })
    trackEvaluation(offerListKey, chainedEvaluation)
    return chainedEvaluation
  }

  const evaluation = evaluateLereSearchPricing(params)
  trackEvaluation(offerListKey, evaluation)
  return evaluation
}
